import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import { toast } from "sonner";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

// Saved homes (the Bookmark toggle). One GET /api/saves per signed-in
// session hydrates both the id set (drives every Bookmark's fill state on
// deck rows and popups) and the listing cards (the "Saved homes" strip on
// /matches). Toggles are optimistic: real accounts roll back honestly when
// the API refuses; offline demo sessions (user.demo) keep saves in memory
// so the flow stays demoable without faking server success.

const SavesContext = createContext(null);

export function SavesProvider({ children }) {
  const { user } = useAuth();
  const [ids, setIds] = useState(() => new Set());
  const [items, setItems] = useState([]);
  const idsRef = useRef(ids);
  idsRef.current = ids;
  const demoRef = useRef(false);
  demoRef.current = Boolean(user?.demo);

  useEffect(() => {
    if (!user) {
      setIds(new Set());
      setItems([]);
      return;
    }
    let alive = true;
    api
      .get("/saves")
      .then((res) => {
        if (!alive) return;
        const list = Array.isArray(res.data?.saves) ? res.data.saves : [];
        setItems(list);
        setIds(new Set(list.map((l) => l.id)));
      })
      .catch(() => {
        // Backend unreachable or refused — demo sessions simply start empty
        // and keep toggles in memory; real sessions retry on next sign-in.
      });
    return () => {
      alive = false;
    };
  }, [user?.id]); // eslint-disable-line react-hooks/exhaustive-deps

  const toggle = useCallback(async (listing) => {
    if (!listing?.id) return;
    const id = listing.id;
    const on = !idsRef.current.has(id);
    const apply = (yes) => {
      setIds((prev) => {
        const next = new Set(prev);
        if (yes) next.add(id);
        else next.delete(id);
        return next;
      });
      setItems((prev) => {
        const without = prev.filter((l) => l.id !== id);
        return yes ? [{ ...listing }, ...without] : without;
      });
    };
    apply(on); // optimistic — the bookmark fills instantly
    toast.success(on ? "Saved" : "Removed from saved");
    try {
      if (on) await api.post(`/saves/${id}`);
      else await api.delete(`/saves/${id}`);
    } catch {
      // Demo sessions keep saves in memory; real accounts roll back honestly.
      if (!demoRef.current) {
        apply(!on);
        toast.error("Could not update your saved homes. Please try again.");
      }
    }
  }, []);

  const value = useMemo(() => ({ ids, items, toggle }), [ids, items, toggle]);

  return <SavesContext.Provider value={value}>{children}</SavesContext.Provider>;
}

export function useSaves() {
  const ctx = useContext(SavesContext);
  if (!ctx) return { ids: new Set(), items: [], toggle: () => {} };
  return ctx;
}
